import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  PriorityHigh as PriorityHighIcon,
  Send as SendIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { useTheme } from '@mui/material/styles';
import { smsApiService } from '../../services/smsApiService';

interface LeadText {
  id: number;
  org_id: number;
  lead_id: string;
  number: string;
  from_number: string;
  message: string;
  direction: string;
  status: string;
  telnyx_message_id: string | null;
  error: string | null;
  created_at: string;
}

interface Lead {
  id: string;
  full_name: string;
  phone: string;
}

interface Conversation {
  lead: Lead;
  messages: LeadText[];
  latestMessage: LeadText;
  hasUnreplied: boolean;
  hasFailed: boolean;
}

interface Props {
  orgId: number | null;
}

const ProLeadConversation: React.FC<Props> = ({ orgId }) => {
  const theme = useTheme();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedConvo, setSelectedConvo] = useState<Conversation | null>(null);
  const [dialogMessages, setDialogMessages] = useState<LeadText[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [convoTab, setConvoTab] = useState(0);

  const fetchConversations = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);

      const PAGE_SIZE = 1000;
      const texts: LeadText[] = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error: textsErr } = await supabase
          .from('lead_texts')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);
        if (textsErr) throw textsErr;
        texts.push(...(data || []));
        hasMore = (data?.length ?? 0) === PAGE_SIZE;
        from += PAGE_SIZE;
      }

      if (texts.length === 0) {
        setConversations([]);
        return;
      }

      const leadIds = Array.from(new Set(texts.map((t: LeadText) => t.lead_id).filter(Boolean)));

      if (leadIds.length === 0) {
        setConversations([]);
        return;
      }

      const BATCH_SIZE = 200;
      const allLeads: Lead[] = [];
      for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
        const batch = leadIds.slice(i, i + BATCH_SIZE);
        const { data, error: leadsErr } = await supabase
          .from('leads')
          .select('id, full_name, phone')
          .in('id', batch);
        if (leadsErr) throw leadsErr;
        allLeads.push(...(data || []));
      }

      const leadMap = new Map<string, Lead>();
      allLeads.forEach((l: Lead) => leadMap.set(l.id, l));

      const grouped = new Map<string, LeadText[]>();
      texts.forEach((t: LeadText) => {
        if (!t.lead_id) return;
        const existing = grouped.get(t.lead_id) || [];
        existing.push(t);
        grouped.set(t.lead_id, existing);
      });

      const convos: Conversation[] = [];
      grouped.forEach((msgs, leadId) => {
        const lead = leadMap.get(leadId);
        if (!lead) return;
        const latest = msgs[0];
        convos.push({
          lead,
          messages: msgs,
          latestMessage: latest,
          hasUnreplied: latest.direction === 'inbound' || latest.status === 'recieved',
          hasFailed: msgs.some((m) => m.status === 'failed'),
        });
      });

      convos.sort((a, b) =>
        new Date(b.latestMessage.created_at).getTime() - new Date(a.latestMessage.created_at).getTime()
      );

      setConversations(convos);
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleOpenConvo = async (convo: Conversation) => {
    setSelectedConvo(convo);
    setReplyText('');
    setDialogLoading(true);
    try {
      const { data, error } = await supabase
        .from('lead_texts')
        .select('*')
        .eq('lead_id', convo.lead.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setDialogMessages(data || []);
    } catch (err: any) {
      console.error('Error fetching conversation messages:', err);
      setDialogMessages(convo.messages.slice().reverse());
    } finally {
      setDialogLoading(false);
    }
  };

  const getConversationFromNumber = (): string | undefined => {
    const outbound = dialogMessages.filter((m) => m.direction === 'outbound');
    if (outbound.length > 0) return outbound[outbound.length - 1].from_number;
    return undefined;
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConvo || !orgId) return;

    const fromNumber = getConversationFromNumber();
    if (!fromNumber) {
      console.error('No from_number found in conversation history');
      return;
    }

    setReplySending(true);
    try {
      const result = await smsApiService.sendSMS(
        selectedConvo.lead.phone,
        replyText.trim(),
        fromNumber
      );

      if (!result.success) throw new Error(result.error || 'Failed to send SMS');

      const { data: inserted, error: insertErr } = await supabase
        .from('lead_texts')
        .insert({
          org_id: orgId,
          lead_id: selectedConvo.lead.id,
          number: selectedConvo.lead.phone,
          from_number: fromNumber,
          message: replyText.trim(),
          direction: 'outbound',
          status: 'sent',
        })
        .select()
        .single();

      if (insertErr) throw insertErr;

      if (inserted) {
        setDialogMessages((prev) => [...prev, inserted]);
      }

      setReplyText('');
      fetchConversations();
    } catch (err: any) {
      console.error('Error sending reply:', err);
    } finally {
      setReplySending(false);
    }
  };

  const failedCount = conversations.filter((c) => c.hasFailed).length;

  const filteredConversations = conversations.filter((c) => {
    if (convoTab === 1 && !c.hasFailed) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.lead.full_name.toLowerCase().includes(q) ||
      c.lead.phone.includes(q)
    );
  });

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Tabs
          value={convoTab}
          onChange={(_, v) => setConvoTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label={`All (${conversations.length})`} />
          <Tab
            label={`Failed (${failedCount})`}
            icon={failedCount > 0 ? <ErrorOutlineIcon sx={{ fontSize: 16 }} /> : undefined}
            iconPosition="start"
            sx={failedCount > 0 ? { color: 'error.main' } : undefined}
          />
        </Tabs>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {convoTab === 0 ? 'Conversations' : 'Failed Messages'} ({filteredConversations.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchConversations}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={40} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {conversations.length === 0
                ? 'No conversations yet. Send a burst text to get started.'
                : 'No conversations match your search.'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredConversations.map((convo, idx) => (
              <React.Fragment key={convo.lead.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  component="div"
                  onClick={() => handleOpenConvo(convo)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    py: 1.5,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: convo.hasUnreplied ? theme.palette.warning.main : theme.palette.primary.main }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {convo.lead.full_name}
                        </Typography>
                        {convo.hasUnreplied && (
                          <PriorityHighIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                        )}
                        {convo.hasFailed && (
                          <Chip label="Failed" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                        <Chip
                          label={convo.lead.phone}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', ml: 1 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          noWrap
                          sx={{ maxWidth: '70%', fontSize: '0.8rem' }}
                        >
                          {convo.latestMessage.direction === 'outbound' ? 'You: ' : ''}
                          {convo.latestMessage.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTime(convo.latestMessage.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Conversation Detail Dialog */}
      <Dialog
        open={!!selectedConvo}
        onClose={() => setSelectedConvo(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedConvo && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedConvo.lead.full_name}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                  {selectedConvo.lead.phone}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedConvo(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {dialogLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={32} />
                </Box>
              ) : dialogMessages.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No messages in this conversation.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, maxHeight: 450, overflowY: 'auto' }}>
                  {dialogMessages.map((msg) => {
                    const isOutbound = msg.direction === 'outbound';
                    return (
                      <Box
                        key={msg.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            p: 1.5,
                            maxWidth: '75%',
                            backgroundColor: isOutbound
                              ? theme.palette.primary.main
                              : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                            color: isOutbound ? '#fff' : theme.palette.text.primary,
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                            {msg.message}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, gap: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.7, fontSize: '0.65rem' }}
                            >
                              {formatTime(msg.created_at)}
                            </Typography>
                            <Chip
                              label={msg.status}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                opacity: 0.8,
                                color: isOutbound ? '#fff' : undefined,
                                borderColor: isOutbound ? 'rgba(255,255,255,0.4)' : undefined,
                              }}
                              variant="outlined"
                            />
                          </Box>
                          {msg.status === 'failed' && msg.error && (
                            <Tooltip title={msg.error} arrow>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <ErrorOutlineIcon sx={{ fontSize: 14, color: isOutbound ? 'rgba(255,200,200,0.9)' : 'error.main' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: isOutbound ? 'rgba(255,200,200,0.9)' : 'error.main',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {msg.error}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {isOutbound && msg.from_number && (
                            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
                              from {msg.from_number}
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
            <Box sx={{ display: 'flex', gap: 1, p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
                multiline
                maxRows={3}
                disabled={replySending}
              />
              <Button
                variant="contained"
                onClick={handleSendReply}
                disabled={replySending || !replyText.trim()}
                sx={{ minWidth: 80 }}
                startIcon={replySending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                {replySending ? 'Sending' : 'Send'}
              </Button>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProLeadConversation;
